/**
 * TenantMiniMap — SAAS-COMPANIES-P2-MAP-TAB.
 *
 * Mini-mapa ENXUTO da aba Mapa do detalhe de uma empresa. Reaproveita as peças
 * de baixo nível do fleet map (useLeaflet + TILE_PROVIDERS + deviceStatusFull),
 * NÃO o MapView monolítico (que se auto-busca a frota global e abre full-viewport).
 *
 * Props:
 *   devices  — lista já filtrada pelo tenant (backend ?tenant_id=), formato
 *              FleetMapDeviceOut (device_id, lat, lng, online, relay, weak_signal,
 *              effective_protected, fw_version, last_seen, customer_name, ...).
 *   center   — { lat, lng, zoom } | null (localStorage por tenant); null → fit-bounds
 *              nos devices geo (centroide/bbox); vazio → fallback fixo.
 *   onSelect — (deviceId) => void  (link "abrir detalhe").
 *   onViewportChange — (center) => void  (para o botão "definir centro aqui").
 */
import { useEffect, useRef, useState } from "react";
import { useLeaflet } from "../fleet/useLeaflet.js";
import { TILE_PROVIDERS } from "../fleet/tileProviders.js";
import { deviceStatusFull } from "../fleet/DeviceMarker.jsx";
import MapStyleSwitcher from "../fleet/MapStyleSwitcher.jsx";

// SAAS-TENANT-PORTAL-MAP-SATELLITE-1 — o mapa do tenant (portal + aba admin)
// INICIA em satélite (pedido do cliente) e permite trocar de basemap com o
// MESMO seletor do fleet principal (normal/satélite/dark/operacional). A
// preferência fica salva por navegador.
const STYLE_KEY = "ipwd-tenant-map-style";
function initialStyle() {
  try {
    const s = localStorage.getItem(STYLE_KEY);
    if (s && TILE_PROVIDERS[s]) return s;
  } catch { /* noop */ }
  return "satellite";
}

// Cor do marcador por status (mesma semântica do fleet map, versão reduzida).
const VARIANT_COLOR = {
  offline: "#6b7280",   // cinza
  "relay-off": "#f59e0b", // âmbar
  action: "#38bdf8",    // azul (ação em execução)
  online: "#22c55e",    // verde
};

const FALLBACK_CENTER = { lat: -10.6822, lng: -39.4914, zoom: 11 };

function markerColor(d) {
  if (d.effective_protected) return "#a855f7"; // roxo = protegido (domina)
  const st = deviceStatusFull(d);
  return VARIANT_COLOR[st.variant] || "#22c55e";
}

function fmtLastSeen(iso) {
  if (!iso) return "sem heartbeat";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s atrás`;
  if (s < 3600) return `${Math.floor(s / 60)}min atrás`;
  if (s < 86400) return `${Math.floor(s / 3600)}h atrás`;
  return `${Math.floor(s / 86400)}d atrás`;
}

function popupHtml(d) {
  const st = deviceStatusFull(d);
  const prot = d.effective_protected ? " · 🔒 Protegido" : "";
  return (
    `<div style="font-size:12px;line-height:1.5">` +
    `<b>${d.device_id}</b><br/>` +
    `${st.tooltip}${prot}<br/>` +
    `Última: ${fmtLastSeen(d.last_seen)}<br/>` +
    `FW: ${d.fw_version || "—"}<br/>` +
    (d.customer_name ? `Cliente: ${d.customer_name}<br/>` : "") +
    `<a href="#" data-open="${d.device_id}" ` +
    `style="color:#38bdf8;text-decoration:underline">abrir detalhe →</a>` +
    `</div>`
  );
}

export default function TenantMiniMap({ devices, center, onSelect, onViewportChange }) {
  const { L, ready } = useLeaflet();
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const tileLayerRef = useRef(null);
  const [mapStyle, setMapStyle] = useState(initialStyle);

  const geo = (devices || []).filter(
    (d) => typeof d.lat === "number" && typeof d.lng === "number"
  );

  // init do mapa (1x, quando Leaflet carrega)
  useEffect(() => {
    if (!ready || !L || !elRef.current || mapRef.current) return;
    const c = center || FALLBACK_CENTER;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true })
      .setView([c.lat, c.lng], c.zoom || FALLBACK_CENTER.zoom);
    const tp = TILE_PROVIDERS[mapStyle] || TILE_PROVIDERS.satellite;
    tileLayerRef.current = L.tileLayer(tp.url, {
      maxZoom: tp.maxZoom, attribution: tp.attribution, subdomains: tp.subdomains || "abc",
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // clique no link "abrir detalhe" dentro do popup
    map.on("popupopen", (e) => {
      const a = e.popup.getElement()?.querySelector("a[data-open]");
      if (a) a.onclick = (ev) => { ev.preventDefault(); onSelect?.(a.getAttribute("data-open")); };
    });
    return () => { map.remove(); mapRef.current = null; tileLayerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, L]);

  // troca de basemap (satélite/normal/dark/operacional) + persistência —
  // mesmo padrão do MapView do fleet principal (setUrl no tileLayer).
  useEffect(() => {
    const tile = tileLayerRef.current;
    if (!tile || !L) return;
    const p = TILE_PROVIDERS[mapStyle] || TILE_PROVIDERS.satellite;
    tile.setUrl(p.url);
    if (tile.options) {
      tile.options.attribution = p.attribution;
      tile.options.maxZoom = p.maxZoom;
      tile.options.subdomains = p.subdomains || "abc";
    }
    try { localStorage.setItem(STYLE_KEY, mapStyle); } catch { /* noop */ }
  }, [L, mapStyle]);

  // recomputa o centro quando `center` muda (ex.: "usar este centro agora")
  useEffect(() => {
    if (!mapRef.current || !center) return;
    mapRef.current.setView([center.lat, center.lng], center.zoom || FALLBACK_CENTER.zoom);
  }, [center]);

  // (re)desenha marcadores + fit-bounds quando NÃO há centro salvo
  useEffect(() => {
    if (!mapRef.current || !layerRef.current || !L) return;
    layerRef.current.clearLayers();
    const pts = [];
    for (const d of geo) {
      const m = L.circleMarker([d.lat, d.lng], {
        radius: 7, color: "#0b1120", weight: 1.5,
        fillColor: markerColor(d), fillOpacity: 0.9,
      });
      m.bindTooltip(d.device_id, { direction: "top" });
      m.bindPopup(popupHtml(d));
      m.addTo(layerRef.current);
      pts.push([d.lat, d.lng]);
    }
    // sem centro explícito → enquadra a frota (centroide/bbox client-side)
    if (!center && pts.length) {
      try {
        mapRef.current.fitBounds(L.latLngBounds(pts).pad(0.2), { maxZoom: 14 });
      } catch { /* bounds inválido (1 ponto) — mantém setView */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, L]);

  // expõe o viewport atual para o botão "definir centro aqui"
  useEffect(() => {
    if (!mapRef.current || !onViewportChange) return;
    const map = mapRef.current;
    const emit = () => {
      const c = map.getCenter();
      onViewportChange({ lat: +c.lat.toFixed(5), lng: +c.lng.toFixed(5), zoom: map.getZoom() });
    };
    map.on("moveend zoomend", emit);
    return () => map.off("moveend zoomend", emit);
  }, [onViewportChange]);

  if (!ready) {
    return (
      <div className="h-[420px] rounded-lg border border-noc-border bg-noc-bg/40
                      flex items-center justify-center text-xs text-noc-muted">
        Carregando mapa…
      </div>
    );
  }
  return (
    <div className="relative">
      <div
        ref={elRef}
        className="h-[420px] rounded-lg border border-noc-border overflow-hidden"
        style={{ background: "#0b1120" }}
      />
      {/* seletor de basemap (satélite/normal/dark/operacional) — top-right p/ não
          colidir com o zoom control (top-left); reusa o componente do fleet */}
      <div className="absolute top-2 right-2 z-[1000]">
        <MapStyleSwitcher currentStyle={mapStyle} onChange={setMapStyle} />
      </div>
    </div>
  );
}
